import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckSquare,
  Camera,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Trash2,
  Upload,
  UploadCloud,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { LocationFilter, type LocationFilterValue } from './LocationFilter';
import { StatCard, StatGrid } from './StatCard';
import { PROGRAM_OPTIONS } from '../constants';
import {
  buildRgfFilename,
  pickExtension,
} from '../utils/buildRgfFilename';
import { validateStagingFile, STAGING_LIMITS } from '../hooks/useStagingUpload';
import {
  useLocalPhotoUpload,
  type LocalUploadEntry,
} from '../hooks/useLocalPhotoUpload';

/**
 * Upload Photos tab — adds a third source alongside Impact and DAM.
 *
 * Flow (matches docs/asset-management-upload-photo-sources.md):
 *   1. User picks (or drops) image files. Each becomes a card with a
 *      thumbnail preview, the canonical RGF filename, file size, and a
 *      remove button. We do NOT upload yet — the staging URL is only
 *      good for 24h, so we wait until the user clicks Upload.
 *   2. User fills the shared metadata form: location cascade (district /
 *      sub-district / village), program, optional event / capture date /
 *      description. `asset_type` is fixed to "Management Photos" to
 *      mirror the Impact ("Impact Photographs") and DAM ("Brandfolder
 *      Photographs") classifications. Title is per-card and defaults to
 *      the RGF filename (sans extension) but is freely editable.
 *   3. Upload → for each picked file, POST `impact/staging-upload`;
 *      then one POST `impact/upload` with the combined assets payload.
 *      Server responses are shown per-card so partial failures don't
 *      take the whole batch down.
 *
 * UX choices intentionally match the existing Impact / DAM tabs:
 *   - same card grid look, same primary/secondary button colours
 *   - same `useNotification` toasts on success/failure
 *   - same role assumption (`IMPACT_ASSET.MODERATOR`) — the route gating
 *     at the App level already restricts who reaches this tab.
 */

const MAX_BATCH = 5;

/**
 * Fixed `asset_type` for every photo uploaded through this source — pairs
 * with Impact's "Impact Photographs" and DAM's "Brandfolder Photographs"
 * so downstream library filters can tell the three apart without any
 * per-upload picker on this tab.
 */
const UPLOAD_ASSET_TYPE = 'Management Photos';

interface SharedMetadata {
  program: string;
  location: LocationFilterValue;
  event: string;
  /** ISO date string (YYYY-MM-DD); empty means "use now()" at filename build. */
  date: string;
  description: string;
}

interface StagedCard {
  id: string;
  file: File;
  /** Object URL for the thumbnail; revoked on remove and on unmount. */
  previewUrl: string;
  /** Editable title — defaults to the RGF filename (sans extension). */
  title: string;
  /**
   * False while the title still mirrors the auto-built canonical filename;
   * flips to true the moment the user types in the title input. We use
   * this to refresh stale "RGF-unknown" defaults as the user fills in
   * location / program later — without ever overwriting an edited title.
   */
  titleEdited: boolean;
  /** Mirror of file size cached so we don't re-read it on every render. */
  sizeBytes: number;
  /** Set after a stage+upload attempt; surfaces per-card success/error. */
  result: 'pending' | 'success' | 'failed';
  /** Error message when `result === 'failed'`. */
  error?: string;
}

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const newId = (): string =>
  (globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}-${Math.random().toString(36).slice(2)}`);

interface UploadPhotosTabProps {
  /**
   * Called after a successful (full or partial) upload so the parent can
   * trigger an Asset Library refetch or navigate the user there.
   */
  onUploaded?: (succeededCount: number) => void;
}

export const UploadPhotosTab: React.FC<UploadPhotosTabProps> = ({ onUploaded }) => {
  const { showSuccess, showError, showInfo } = useNotification();

  const [cards, setCards] = useState<StagedCard[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [metadata, setMetadata] = useState<SharedMetadata>({
    program: '',
    location: {},
    event: '',
    date: '',
    description: '',
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { runUpload, progress, error: uploadError, reset } = useLocalPhotoUpload();
  const isUploading = progress.isStaging || progress.isUploading;

  // Revoke object URLs on unmount so we don't leak memory.
  useEffect(() => {
    return () => {
      cards.forEach((c) => URL.revokeObjectURL(c.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── File ingestion ────────────────────────────────────────────────────
  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming);
      if (list.length === 0) return;

      const rejected: string[] = [];
      const accepted: StagedCard[] = [];

      let remainingSlots = MAX_BATCH - cards.length;
      for (const file of list) {
        if (remainingSlots <= 0) {
          rejected.push(`${file.name} (max ${MAX_BATCH} per batch)`);
          continue;
        }
        const guard = validateStagingFile(file);
        if (guard) {
          rejected.push(guard);
          continue;
        }
        const previewUrl = URL.createObjectURL(file);
        // Initial title = canonical RGF filename without extension; can be
        // freely edited later. We use defaults for location / program here
        // because they may not be filled in yet — we re-derive on upload.
        const initialFilename = buildRgfFilename({
          program: metadata.program,
          villageName: metadata.location.village_name,
          villageId: metadata.location.village_id,
          districtName: metadata.location.district,
          // Default to "now" when no capture date is set — matches what
          // we send at upload time, so the user never sees a stray
          // "unknown" segment on a card whose metadata is filled in.
          date: metadata.date ? new Date(metadata.date) : new Date(),
          ext: pickExtension(file),
        });
        const titleDefault = initialFilename.replace(/\.[^.]+$/, '');
        accepted.push({
          id: newId(),
          file,
          previewUrl,
          title: titleDefault,
          titleEdited: false,
          sizeBytes: file.size,
          result: 'pending',
        });
        remainingSlots -= 1;
      }

      if (rejected.length > 0) {
        showError('Some files skipped', rejected.join('\n'));
      }
      if (accepted.length > 0) {
        setCards((prev) => [...prev, ...accepted]);
      }
    },
    [cards.length, metadata.program, metadata.location, showError],
  );

  const onFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) addFiles(e.target.files);
      // Reset so the same file can be re-picked after a remove.
      e.target.value = '';
    },
    [addFiles],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const removeCard = useCallback((id: string) => {
    setCards((prev) => {
      const target = prev.find((c) => c.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((c) => c.id !== id);
    });
  }, []);

  const updateCardTitle = useCallback((id: string, title: string) => {
    setCards((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title, titleEdited: true } : c)),
    );
  }, []);

  /**
   * When program / location change, any card whose title still mirrors the
   * auto-built canonical filename should refresh to reflect the new metadata.
   * We never touch cards where `titleEdited === true` so user edits survive.
   */
  useEffect(() => {
    setCards((prev) => {
      let changed = false;
      const next = prev.map((card) => {
        if (card.titleEdited) return card;
        const filename = buildRgfFilename({
          program: metadata.program,
          villageName: metadata.location.village_name,
          villageId: metadata.location.village_id,
          districtName: metadata.location.district,
          // Same fallback as initial card add + upload time — `null`
          // would render as "unknown" and make a card look broken even
          // though the other metadata is filled in.
          date: metadata.date ? new Date(metadata.date) : new Date(),
          ext: pickExtension(card.file),
        });
        const nextTitle = filename.replace(/\.[^.]+$/, '');
        if (nextTitle === card.title) return card;
        changed = true;
        return { ...card, title: nextTitle };
      });
      return changed ? next : prev;
    });
  }, [metadata.program, metadata.location, metadata.date]);

  const clearAll = useCallback(() => {
    cards.forEach((c) => URL.revokeObjectURL(c.previewUrl));
    setCards([]);
    reset();
  }, [cards, reset]);

  // ── Validation before submit ──────────────────────────────────────────
  const validationIssue = useMemo<string | null>(() => {
    if (cards.length === 0) return 'Add at least one photo.';
    if (!metadata.program) return 'Select a program.';
    if (!metadata.location.village_id) return 'Pick a village (district → sub-district → village).';
    const missingTitle = cards.find((c) => !c.title.trim());
    if (missingTitle) return 'Every card needs a title.';
    return null;
  }, [cards, metadata.program, metadata.location.village_id]);

  // ── Submit ────────────────────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (validationIssue) {
      showError('Cannot upload yet', validationIssue);
      return;
    }
    if (cards.length === 0) return;

    const captureDate = metadata.date ? new Date(metadata.date) : new Date();

    const entries: LocalUploadEntry[] = cards.map((card) => {
      const ext = pickExtension(card.file);
      // Canonical filename — keep it synced with whatever the user finally
      // typed in location / program. This is what Brandfolder + NocoDB
      // will store; the title is a separate display field.
      const filename = buildRgfFilename({
        program: metadata.program,
        villageName: metadata.location.village_name,
        villageId: metadata.location.village_id,
        districtName: metadata.location.district,
        date: captureDate,
        ext,
      });
      return {
        id: card.id,
        file: card.file,
        asset: {
          id: card.id,
          title: card.title.trim() || filename.replace(/\.[^.]+$/, ''),
          description: metadata.description.trim() || undefined,
          type: 'Image',
          village_id: metadata.location.village_id,
          program: metadata.program,
          asset_type: UPLOAD_ASSET_TYPE,
          event: metadata.event.trim() || undefined,
          date: metadata.date
            ? new Date(metadata.date).toISOString()
            : new Date().toISOString(),
          filename,
        },
      };
    });

    const result = await runUpload(entries);
    if (!result) {
      return; // Hook already set `uploadError`; the banner renders below.
    }

    const successIds = new Set(result.success.map((s) => s.id));
    const failedById = new Map(result.failed.map((f) => [f.id, f.error]));

    setCards((prev) =>
      prev.map((c) =>
        successIds.has(c.id)
          ? { ...c, result: 'success' }
          : failedById.has(c.id)
          ? { ...c, result: 'failed', error: failedById.get(c.id) }
          : c,
      ),
    );

    if (result.success.length > 0 && result.failed.length === 0) {
      showSuccess(
        'Photos Uploaded',
        `${result.success.length} photo${result.success.length === 1 ? '' : 's'} added to the asset library.`,
      );
    } else if (result.success.length > 0 && result.failed.length > 0) {
      showInfo(
        'Partial Upload',
        `${result.success.length} succeeded, ${result.failed.length} failed. See per-card status.`,
      );
    } else {
      showError(
        'Upload Failed',
        result.failed[0]?.error || 'No photos were uploaded.',
      );
    }

    if (result.success.length > 0) {
      onUploaded?.(result.success.length);
    }
  }, [
    cards,
    metadata,
    runUpload,
    showSuccess,
    showError,
    showInfo,
    onUploaded,
    validationIssue,
  ]);

  // ── Render ────────────────────────────────────────────────────────────
  const remainingSlots = Math.max(0, MAX_BATCH - cards.length);

  // Friendly readouts for the stats grid below
  const programLabel = useMemo(() => {
    if (!metadata.program) return 'Not set';
    return PROGRAM_OPTIONS.find((opt) => opt.value === metadata.program)?.label ?? metadata.program;
  }, [metadata.program]);
  const locationLabel = useMemo(() => {
    if (metadata.location.village_name) return metadata.location.village_name;
    if (metadata.location.district) return metadata.location.district;
    return 'Not set';
  }, [metadata.location]);
  const readyLabel = validationIssue ? 'Not ready' : 'Ready';

  return (
    <div className="space-y-6">
      {/* Stats specific to Upload progress */}
      <StatGrid>
        <StatCard
          icon={ImageIcon}
          label="Photos Added"
          value={`${cards.length} / ${MAX_BATCH}`}
          variant="blue"
        />
        <StatCard
          icon={MapPin}
          label="Location"
          value={locationLabel}
          variant="green"
          compact
        />
        <StatCard
          icon={Camera}
          label="Program"
          value={programLabel}
          variant="purple"
          compact
        />
        <StatCard
          icon={Upload}
          label="Status"
          value={readyLabel}
          variant="orange"
          compact
        />
      </StatGrid>

      {/* Drop zone / file picker */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!isDragging) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <UploadCloud className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            Drop photos here, or
          </h3>
          <Button
            variant="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={remainingSlots === 0 || isUploading}
            className="mt-2"
          >
            <Upload className="h-4 w-4 mr-2" />
            Browse files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={STAGING_LIMITS.allowedMimeTypes.join(',')}
            onChange={onFileInput}
            className="hidden"
          />
          <p className="text-xs text-gray-500 mt-4">
            JPEG, PNG, WebP or GIF · up to 50 MB · {MAX_BATCH} per batch
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {cards.length}/{MAX_BATCH} added
          </p>
        </div>
      </div>

      {/* Cards grid */}
      {cards.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">
              Selected photos ({cards.length})
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={clearAll}
              disabled={isUploading}
            >
              Clear all
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((card) => (
              <div
                key={card.id}
                className={`border rounded-lg overflow-hidden bg-white ${
                  card.result === 'success'
                    ? 'border-green-300'
                    : card.result === 'failed'
                    ? 'border-red-300'
                    : 'border-gray-200'
                }`}
              >
                <div className="relative aspect-[4/3] bg-gray-100">
                  {/* Local preview via createObjectURL — never hits the network */}
                  <img
                    src={card.previewUrl}
                    alt={card.title}
                    className="w-full h-full object-cover"
                  />
                  {!isUploading && card.result !== 'success' && (
                    <button
                      type="button"
                      onClick={() => removeCard(card.id)}
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-700 rounded-full p-1.5 shadow"
                      aria-label={`Remove ${card.title}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {card.result === 'success' && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1 shadow">
                      <CheckSquare className="h-3.5 w-3.5" />
                    </div>
                  )}
                  {card.result === 'failed' && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 shadow">
                      <AlertCircle className="h-3.5 w-3.5" />
                    </div>
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <input
                    type="text"
                    value={card.title}
                    onChange={(e) => updateCardTitle(card.id, e.target.value)}
                    disabled={isUploading || card.result === 'success'}
                    maxLength={255}
                    placeholder="Asset title"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                  />
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="truncate" title={card.file.name}>
                      {card.file.name}
                    </span>
                    <span className="flex-shrink-0 ml-2">{formatBytes(card.sizeBytes)}</span>
                  </div>
                  {card.result === 'failed' && card.error && (
                    <div className="text-xs text-red-600 flex items-start gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                      <span>{card.error}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shared metadata form — placed AFTER the cards grid (and before the
          submit button) so the user sees their selected photos right under
          the drop zone, then fills in the metadata they want applied to
          every one, then commits with Upload. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <MapPin className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="font-medium text-gray-900">Shared metadata</h3>
          <span className="ml-2 text-xs text-gray-500">applies to every photo in this batch</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Location cascade — reuses the same widget Impact + Library use */}
          <div>
            <LocationFilter
              value={metadata.location}
              onChange={(loc) =>
                setMetadata((prev) => ({ ...prev, location: loc }))
              }
              showVillages
              disabled={isUploading}
            />
          </div>

          {/* Right column: program, asset type, event, date, description */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Program *
              </label>
              <select
                value={metadata.program}
                onChange={(e) =>
                  setMetadata((prev) => ({ ...prev, program: e.target.value }))
                }
                disabled={isUploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select program</option>
                {PROGRAM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event (optional)
                </label>
                <input
                  type="text"
                  value={metadata.event}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, event: e.target.value }))
                  }
                  disabled={isUploading}
                  placeholder="e.g. Site visit"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capture date
                </label>
                <input
                  type="date"
                  value={metadata.date}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, date: e.target.value }))
                  }
                  disabled={isUploading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={metadata.description}
                onChange={(e) =>
                  setMetadata((prev) => ({ ...prev, description: e.target.value }))
                }
                disabled={isUploading}
                rows={2}
                maxLength={1000}
                placeholder="Shared across every photo in this batch"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Banner: upload error from the hook */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Upload Error</h3>
              <p className="mt-1 text-sm text-red-700">{uploadError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Banner: client-side validation hint */}
      {!isUploading && cards.length > 0 && validationIssue && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <div className="ml-3">
              <p className="text-sm text-amber-800">{validationIssue}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit row */}
      {cards.length > 0 && (
        <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600">
            {isUploading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {progress.isStaging
                  ? `Staging ${progress.staged + progress.failedStaging}/${progress.total}…`
                  : `Uploading ${progress.uploaded}/${progress.total}…`}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                {cards.length} photo{cards.length === 1 ? '' : 's'} ready
              </span>
            )}
          </div>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={isUploading || !!validationIssue}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload {cards.length} Photo{cards.length === 1 ? '' : 's'}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
